import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import LeaveListView from "./LeaveListView";
import LeaveCalendar from "./LeaveCalendar";
import LeavePolicies, { GET_LEAVE_POLICIES } from "./LeavePolicies";
import LeaveBalances from "./LeaveBalances";
import LeaveReports from "./LeaveReports";
import LeaveDisputes from "./LeaveDisputes";
import LeaveAttendance from "./LeaveAttendance";
import LeaveFormModal from "./LeaveFormModal";
import { FALLBACK_LEAVE_TYPE_OPTIONS, GET_EMPLOYEES } from "./leaveShared";

// Leave Requests: the page shell. Each view lives in its own component:
//   list      LeaveListView (filters, summary, table, review/edit modals)
//   calendar  LeaveCalendar
//   policies  LeavePolicies
//   balances  LeaveBalances
//   reports   LeaveReports
//   attendance LeaveAttendance (Mark present per day)
//   disputes  LeaveDisputes (deductions questioned from the app)

const VIEWS = ["list", "calendar", "attendance", "reports", "disputes", "policies", "balances"];

const LeaveManagement = () => {
  // Deep links (Staff Desk): ?view=disputes, ?leave=<id> opens that leave
  const [params] = useSearchParams();
  const focusLeaveId = params.get("leave");
  const [view, setView] = useState(() => {
    const v = params.get("view");
    return VIEWS.includes(v) ? v : "list";
  });
  const [marking, setMarking] = useState(false);

  const { data: empData } = useQuery(GET_EMPLOYEES, {
    variables: { data: {} },
    fetchPolicy: "network-only",
  });
  const employees = empData?.getEmployeelist?.employee || [];

  const { data: policyData } = useQuery(GET_LEAVE_POLICIES, {
    fetchPolicy: "cache-and-network",
  });
  // Generic policies only: one option per leave type
  const policyList = (policyData?.getLeavePolicies?.policies || []).filter((p) => !p.employeeId);
  const leaveTypeOptions = policyList.length
    ? policyList.map((p) => ({
        value: p.leaveType,
        label: p.isActive ? p.label : `${p.label} (inactive)`,
      }))
    : FALLBACK_LEAVE_TYPE_OPTIONS;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="p-6 bg-white w-full rounded-lg shadow-md m-4 overflow-y-auto">
          <div className="flex justify-between mb-6 border-b pb-4">
            <h1 className="text-2xl font-semibold text-gray-800">Leave Requests</h1>
            <div className="flex items-center gap-3">
              <div className="flex rounded border overflow-hidden text-sm">
                {VIEWS.map((v) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-3 py-2 capitalize ${
                      view === v ? "bg-gray-800 text-white" : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setMarking(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition"
              >
                Mark Leave Directly
              </button>
            </div>
          </div>

          {view === "list" && (
            <LeaveListView focusLeaveId={focusLeaveId} employees={employees} leaveTypeOptions={leaveTypeOptions} />
          )}
          {view === "calendar" && <LeaveCalendar />}
          {view === "reports" && <LeaveReports />}
          {view === "attendance" && <LeaveAttendance />}
          {view === "disputes" && <LeaveDisputes />}
          {view === "policies" && <LeavePolicies />}
          {view === "balances" && <LeaveBalances />}
        </div>
      </Layout>

      {marking && (
        <LeaveFormModal
          employees={employees}
          leaveTypeOptions={leaveTypeOptions}
          onClose={() => setMarking(false)}
        />
      )}
    </div>
  );
};

export default LeaveManagement;
